import { Formik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Space,
  Tooltip,
  Switch,
  Tag,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Edit3, FlaskConical, Trash2 } from "lucide-react";

const { TextArea } = Input;

const validationSchema = Yup.object().shape({
  sample_id: Yup.string().nullable().required("Sample is required"),
  quantity: Yup.number().nullable().required("Quantity is required"),
  to_id: Yup.string().nullable().required("Department is required"),
  test_type_id: Yup.string().nullable().required("Test type is required"),
  parameter_id: Yup.string().nullable(),
  min_value: Yup.number()
    .nullable()
    .when("parameter_id", {
      is: (val) => val !== null && val !== "",
      then: (schema) =>
        schema.required("Min value is required when parameter is selected"),
    }),
  max_value: Yup.number()
    .nullable()
    .when(["parameter_id", "min_value"], {
      is: (parameter_id, min_value) =>
        parameter_id !== null && parameter_id !== "" && min_value !== null,
      then: (schema) =>
        schema
          .required("Max value is required when parameter is selected")
          .min(
            Yup.ref("min_value"),
            "Max value must be greater than or equal to min value"
          ),
    }),
  description: Yup.string()
    .nullable()
    .when("parameter_id", {
      is: (val) => val !== null && val !== "",
      then: (schema) =>
        schema.required("Description is required when parameter is selected"),
    }),
  sample_instruction: Yup.string().nullable(),
  sampling_min_value: Yup.number()
    .nullable()
    .when("is_qc_sampling", {
      is: true,
      then: (schema) => schema.required("Min value is required for QC sampling"),
    }),
  sampling_max_value: Yup.number()
    .nullable()
    .when(["is_qc_sampling", "sampling_min_value"], {
      is: (is_qc_sampling, sampling_min_value) =>
        is_qc_sampling === true && sampling_min_value !== null && sampling_min_value !== undefined,
      then: (schema) =>
        schema
          .required("Max value is required for QC sampling")
          .min(
            Yup.ref("sampling_min_value"),
            "Max value must be greater than or equal to min value"
          ),
    }),
});

export default function SamplingForm({
  editIndex,
  onSubmit = (values) => console.log("Submitted values:", values),
  inventory_list = [],
  department_list = [],
  testing_type_list = [],
  parameter_list = [],
  values = {
    sample_id: null,
    quantity: null,
    unit: "",
    to_id: null,
    test_type_id: null,
    parameter_id: null,
    min_value: null,
    max_value: null,
    description: null,
    sample_instruction: null,
    is_qc_sampling: false,
    sampling_min_value: null,
    sampling_max_value: null,
  },

}) {
  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleSubmit }) => {
        return (
          <Form layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              {/* Sample */}
              <Col span={8}>
                <Form.Item
                  label="Sample"
                  validateStatus={
                    touched.sample_id && errors.sample_id ? "error" : ""
                  }
                  help={touched.sample_id && errors.sample_id}
                >
                  <Space.Compact className="w-full">
                    <Select
                      value={values.sample_id}
                      placeholder="Select Sample"
                      onChange={(val, option) => {
                        setFieldValue("sample_id", val || null);
                        if (val) {
                          setFieldValue("sample", option.label || null);
                          setFieldValue("unit", option.unit);
                        } else {
                          setFieldValue("unit", "");
                        }
                      }}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={inventory_list
                        ?.filter((item) => item?.material_type === "By Product")
                        ?.map((item) => ({
                          label: item.material_name,
                          value: item.material_id,
                          unit: item.unit,
                        }))}
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* Quantity */}
              <Col span={8}>
                <Form.Item
                  label="Quantity"
                  validateStatus={
                    touched.quantity && errors.quantity ? "error" : ""
                  }
                  help={touched.quantity && errors.quantity}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Quantity"
                    value={values.quantity}
                    min={0}
                    addonAfter={values.unit}
                    onChange={(val) => setFieldValue("quantity", val)}
                  />
                </Form.Item>
              </Col>

              {/* Department */}
              <Col span={8}>
                <Form.Item
                  label="To Department"
                  validateStatus={touched.to_id && errors.to_id ? "error" : ""}
                  help={touched.to_id && errors.to_id}
                >
                  <Space.Compact className="w-full">
                    <Select
                      value={values.to_id}
                      placeholder="Select Department"
                      onChange={(val, option) => {
                        setFieldValue("to_id", val || null);
                        if (val) {
                          setFieldValue("to", option.label);
                          const name = (option.label || '').toLowerCase().trim();
                          const isQC = name.includes('quality') || name.includes('qc') || name.includes('quality check');
                          if (isQC) setFieldValue("is_qc_sampling", true);
                        }
                      }}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      style={{ width: "100%" }}
                      options={department_list?.map((dept) => ({
                        label: dept.department_name,
                        value: dept.id,
                      }))}
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* Test Type */}
              <Col span={8}>
                <Form.Item
                  label="Test Type"
                  validateStatus={
                    touched.test_type_id && errors.test_type_id ? "error" : ""
                  }
                  help={touched.test_type_id && errors.test_type_id}
                >
                  <Space.Compact className="w-full">
                    <Select
                      value={values.test_type_id}
                      placeholder="Select Test Type"
                      onChange={(val, option) => {
                        setFieldValue("test_type_id", val || null);
                        if (val) {
                          setFieldValue("test_type", option.label);
                        }
                      }}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      style={{ width: "100%" }}
                      options={testing_type_list?.map((test) => ({
                        label: test.testing_type,
                        value: test.testing_type_id,
                      }))}
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* QC Min / Max — inline after Test Type, only when QC enabled */}
              {values.is_qc_sampling && (
                <>
                  <Col span={8}>
                    <Form.Item
                      label="Sampling Min Value"
                      validateStatus={
                        touched.sampling_min_value && errors.sampling_min_value ? "error" : ""
                      }
                      help={touched.sampling_min_value && errors.sampling_min_value}
                    >
                      <InputNumber
                        className="w-full"
                        placeholder="Min value"
                        value={values.sampling_min_value}
                        onChange={(val) => setFieldValue("sampling_min_value", val)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label="Sampling Max Value"
                      validateStatus={
                        touched.sampling_max_value && errors.sampling_max_value ? "error" : ""
                      }
                      help={touched.sampling_max_value && errors.sampling_max_value}
                    >
                      <InputNumber
                        className="w-full"
                        placeholder="Max value"
                        value={values.sampling_max_value}
                        onChange={(val) => setFieldValue("sampling_max_value", val)}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {/* Conditional Parameter */}
              <Col span={8}>
                <Form.Item
                  label="Conditional Parameter"
                  validateStatus={
                    touched.parameter_id && errors.parameter_id ? "error" : ""
                  }
                  help={touched.parameter_id && errors.parameter_id}
                >
                  <Space.Compact className="w-full">
                    <Select
                      id="parameter_id"
                      name="parameter_id"
                      value={values.parameter_id}
                      onChange={(val, option) => {
                        setFieldValue("parameter_id", val || null);
                        if (val) {
                          setFieldValue("parameter", option.label || null);
                          setFieldValue("parameter_unit", option.parameter_unit);
                        } else {
                          setFieldValue("parameter_unit", null);
                        }
                      }}
                      showSearch
                      placeholder="Select Parameter"
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={parameter_list?.map((param) => ({
                        value: param.id,
                        label: param.parameter_name,
                        parameter_unit: param.unit,
                      }))}
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* Min Value (conditional parameter) */}
              <Col span={4}>
                <Form.Item
                  label="Min Value"
                  validateStatus={
                    touched.min_value && errors.min_value ? "error" : ""
                  }
                  help={touched.min_value && errors.min_value}
                >
                  <Space.Compact className="w-full">
                    <InputNumber
                      className="w-full"
                      placeholder={`Min ${values.parameter_unit ? `in ${values.parameter_unit}` : ""}`}
                      value={values.min_value}
                      onChange={(val) => setFieldValue("min_value", val)}
                      addonAfter={
                        <Tooltip title={values.parameter_unit}>
                          <span>
                            {values.parameter_unit?.length > 4
                              ? values.parameter_unit?.slice(0, 4) + "..."
                              : values.parameter_unit}
                          </span>
                        </Tooltip>
                      }
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* Max Value (conditional parameter) */}
              <Col span={4}>
                <Form.Item
                  label="Max Value"
                  validateStatus={
                    touched.max_value && errors.max_value ? "error" : ""
                  }
                  help={touched.max_value && errors.max_value}
                >
                  <Space.Compact className="w-full">
                    <InputNumber
                      className="w-full"
                      placeholder={`Max ${values.parameter_unit ? `in ${values.parameter_unit}` : ""}`}
                      value={values.max_value}
                      onChange={(val) => setFieldValue("max_value", val)}
                      addonAfter={
                        <Tooltip title={values.parameter_unit}>
                          <span>
                            {values.parameter_unit?.length > 4
                              ? values.parameter_unit?.slice(0, 4) + "..."
                              : values.parameter_unit}
                          </span>
                        </Tooltip>
                      }
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* Description */}
              <Col span={8}>
                <Form.Item
                  label="Description"
                  validateStatus={
                    touched.description && errors.description ? "error" : ""
                  }
                  help={touched.description && errors.description}
                >
                  <TextArea
                    rows={2}
                    placeholder="Description"
                    value={values.description}
                    onChange={(e) => setFieldValue("description", e.target.value)}
                  />
                </Form.Item>
              </Col>

              {/* Sample Instruction */}
              <Col span={16}>
                <Form.Item
                  label="Sample Instruction"
                  validateStatus={
                    touched.sample_instruction && errors.sample_instruction ? "error" : ""
                  }
                  help={touched.sample_instruction && errors.sample_instruction}
                >
                  <TextArea
                    rows={2}
                    placeholder="Instruction"
                    value={values.sample_instruction}
                    onChange={(e) =>
                      setFieldValue("sample_instruction", e.target.value)
                    }
                  />
                </Form.Item>
              </Col>

              {/* QC Switch */}
              <Col span={8}>
                <Form.Item label="QC">
                  <Switch
                    checked={values.is_qc_sampling}
                    onChange={(val) => {
                      setFieldValue("is_qc_sampling", val);
                      if (!val) {
                        setFieldValue("sampling_min_value", null);
                        setFieldValue("sampling_max_value", null);
                      }
                    }}
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                  />
                </Form.Item>
              </Col>


              {/* Submit Button */}
              <Col span={24}>
                <Button type="primary" htmlType="submit" block>
                  {editIndex !== null ? "Update" : "Add"}
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
}

export const SamplingCard = ({ fromView, item, onEdit, onDelete }) => {
  const {
    sample,
    sample_id,
    quantity,
    unit,
    to,
    to_id,
    test_type,
    test_type_id,
    parameter,
    parameter_id,
    parameter_unit,
    min_value,
    max_value,
    description,
    sample_instruction,
    is_qc_sampling,
    sampling_min_value,
    sampling_max_value,
  } = item;

  const showLabel = (label, id) => label || `${id ? "ID: " + id : "N/A"}`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition p-3 text-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-pink-100 rounded-md">
            <FlaskConical className="w-4 h-4 text-pink-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p
                className="font-semibold text-gray-900 leading-tight truncate text-base w-48"
                title={showLabel(sample, sample_id)}
              >
                {showLabel(sample, sample_id)}
              </p>
              {is_qc_sampling ? (
                <Tag color="gold" className="text-xs font-medium shrink-0">QC</Tag>
              ) : (
                <Tag color="default" className="text-xs font-medium shrink-0">INLINE</Tag>
              )}
            </div>
            <p
              className="text-xs text-gray-600 truncate"
              title={`Sent To: ${showLabel(to, to_id)}`}
            >
              Department: {showLabel(to, to_id)}
            </p>
          </div>
        </div>

        {!fromView && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                onClick={() => onEdit()}
                title="Edit sample"
              >
                <Edit3 className="w-4 h-4 text-blue-600" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                onClick={() => onDelete()}
                title="Delete sample"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-indigo-50 rounded-md p-2.5">
          <p className="text-xs text-indigo-700 font-medium mb-1">Quantity</p>
          <p className="text-indigo-900 font-semibold">
            {quantity}{" "}
            <span className="text-sm font-normal text-indigo-700">
              {unit || "Unit"}
            </span>
          </p>
        </div>

        <div className="bg-purple-50 rounded-md p-2.5">
          <p className="text-xs text-purple-700 font-medium mb-1">Test Type</p>
          <p
            className="text-purple-900 font-semibold truncate"
            title={showLabel(test_type, test_type_id)}
          >
            {showLabel(test_type, test_type_id)}
          </p>
        </div>

        <div className="bg-green-50 rounded-md p-2.5">
          <p className="text-xs text-green-700 font-medium mb-1">Parameter</p>
          <div className="flex justify-between items-center">
            <p
              className="text-green-900 font-semibold truncate mr-2"
              title={showLabel(parameter, parameter_id)}
            >
              {showLabel(parameter, parameter_id)}
            </p>
            <span className="text-xs text-green-700 font-medium bg-green-100 px-1.5 py-0.5 rounded">
              {parameter_unit || "N/A"}
            </span>
          </div>
        </div>

        {(Number(min_value) >= 0 || Number(max_value) >= 0) && (
          <div className="bg-orange-50 rounded-md p-2.5">
            <p className="text-xs text-orange-700 font-medium mb-1">Range</p>
            <p className="text-orange-900 font-semibold">
              {min_value !== null ? min_value : "N/A"} -{" "}
              {max_value !== null ? max_value : "N/A"}{" "}
              <span className="text-sm font-normal text-orange-700">
                {parameter_unit}
              </span>
            </p>
          </div>
        )}

        {/* QC Min / Max — only when QC enabled and values present */}
        {is_qc_sampling && (sampling_min_value !== null || sampling_max_value !== null) && (
          <div className="bg-yellow-50 rounded-md p-2.5 col-span-2">
            <p className="text-xs text-yellow-700 font-medium mb-1">QC Range</p>
            <p className="text-yellow-900 font-semibold">
              {sampling_min_value !== null ? sampling_min_value : "N/A"}
              {" – "}
              {sampling_max_value !== null ? sampling_max_value : "N/A"}
            </p>
          </div>
        )}
      </div>

      {/* Description */}
      {description && (
        <div className="mt-3 bg-gray-50 rounded-md p-2.5">
          <p className="text-xs text-gray-600 font-medium mb-1">Description</p>
          <p className="text-gray-800 text-sm leading-relaxed">{description}</p>
        </div>
      )}

      {/* Instruction */}
      {sample_instruction && (
        <div className="mt-3 bg-gray-50 rounded-md p-2.5">
          <p className="text-xs text-gray-600 font-medium mb-1">Instruction</p>
          <p className="text-gray-800 italic text-sm leading-relaxed">
            {sample_instruction}
          </p>
        </div>
      )}
    </div>
  );
};
